import { useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { darken } from '@mui/material/styles';
import { alpha, Box, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntity, VersionGroupedByParent } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { fetchTitleV2 } from '@/redux/slices/titleV2Slice';
import { fetchTitle } from '@/redux/slices/orderPlacementSlice';
import { makeSelectVersionHierarchyEntities, selectGroupedVersionSelections, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { isTitleEntityV2 } from '@/_utils/utils';
import ScheduleTitleAccordion from '@/components/Focus/AvailsSchedule/NewSchedule/TitleSearch/ScheduleSelectedVersions/ScheduleTitleAccordion';
import ScheduleTitleCard from '@/components/Focus/AvailsSchedule/NewSchedule/TitleSearch/ScheduleSelectedVersions/ScheduleTitleCard';
import { useIncompatibleVersionSelectionConfirmation } from './useIncompatibleVersionSelectionConfirmation.hook';

function extractKeys(obj: VersionGroupedByParent) {
  let keys: number[] = [];
  for (const key in obj) {
    if (key.startsWith('_')) {
      keys.push(parseInt(key.replace('_', '')));
      if (typeof obj[key] === 'object') {
        keys = keys.concat(extractKeys(obj[key]));
      }
    }
  }
  return Array.from(new Set(keys));
}

type SelectedVersionGroupHeaderProps = {
  count?: number;
  parentKey: string;
};

const SelectedVersionGroupHeader = ({ count, parentKey }: SelectedVersionGroupHeaderProps) => {
  const versionsByKey = useAppSelector(selectVersionKeys);
  const selectVersionHierarchyDisplay = useMemo(makeSelectVersionHierarchyEntities, []);

  const display = useAppSelector((state) => selectVersionHierarchyDisplay(state, parentKey.replace('_', '')));
  const parentTitle = versionsByKey[parentKey.replace('_', '')] || {};

  return (
    <>
      <TitleName>{display.seriesEntity?.name || parentTitle.name}</TitleName>
      <SelectedEpisodeCount>
        {count ?? display.episodeCount} Version{(count ?? display.episodeCount) !== 1 ? 's' : ''} Selected
      </SelectedEpisodeCount>
    </>
  );
};

type ScheduleSelectedVersionsProps = {
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
  onVersionClick?: (version: TitleEntity | TitleEntityV2) => void;
};

const ScheduleSelectedVersions = ({ selectedVersions, onVersionClick }: ScheduleSelectedVersionsProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const groupedByParent = useAppSelector(selectGroupedVersionSelections);
  const versionsByKey = useAppSelector(selectVersionKeys);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { incompatibleVersionSelectionConfirmation } = useIncompatibleVersionSelectionConfirmation();

  const selectTitleById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[titleKey] || {};
  };

  const toggleExpand = (accordionKey: string) => {
    setExpanded((prev) => (prev.includes(accordionKey) ? prev.filter((key) => key !== accordionKey) : [...prev, accordionKey]));
  };

  useEffect(
    function fetchDataOfMissingTitleVersionsFromGroupedParent() {
      // get every key in selectedVersions so we can make sure we have all the keys we need
      const allKeys = extractKeys(groupedByParent);
      const fetchedVersionKeys = Object.keys(versionsByKey);
      // find any keys from allKeys that do not exist in fetchedVersionKeys
      const missingVersionKeys = allKeys.filter((key) => !fetchedVersionKeys.includes(String(key)));
      if (missingVersionKeys.length === 0 || (missingVersionKeys.length === 1 && missingVersionKeys[0] === 0)) return;

      setLoading(true);
      const promises = missingVersionKeys.map((key) => {
        if (isTitleV2) {
          return dispatch(fetchTitleV2(key));
        }
        return dispatch(fetchTitle(key));
      });
      // retrieve all the missing versions
      Promise.all(promises);
    },
    [groupedByParent]
  );

  useEffect(
    function checkIfMissingTitleVersionsFromGroupedParentHasBeenFetched() {
      if (!loading) return;
      const allKeys = extractKeys(groupedByParent);
      const fetchedVersionKeys = Object.keys(versionsByKey);
      // find any keys from allKeys that do not exist in fetchedVersionKeys
      const missingVersionKeys = allKeys.filter((key) => !fetchedVersionKeys.includes(String(key)));
      if (missingVersionKeys.length === 0 || (missingVersionKeys.length === 1 && missingVersionKeys[0] === 0)) {
        setLoading(false);
        return;
      }
    },
    [versionsByKey]
  );

  return (
    <Box>
      {loading ? (
        'Loading...'
      ) : (
        <>
          {Object.keys(groupedByParent).map((parentKey) => {
            const titleChildren = groupedByParent[parentKey];

            return (
              <TitleContainer key={parentKey}>
                <SelectedVersionGroupHeader count={selectedVersions.length} parentKey={parentKey} />

                {Object.keys(titleChildren).map((titleKey) => {
                  const title = selectTitleById(titleKey);
                  const isVersionChildren = isTitleEntityV2(title)
                    ? (title.relationships?.children?.every((c) => c.entity_type === 'version') ?? false)
                    : (title.children?.every((c) => c.entity_type === 'version') ?? false);

                  if (isVersionChildren) {
                    return <ScheduleTitleCard key={titleKey} title={title} titleGroup={titleChildren[titleKey]} />;
                  }

                  return (
                    <ScheduleTitleAccordion
                      key={titleKey}
                      title={title}
                      titleGroup={titleChildren[titleKey]}
                      selectedVersions={selectedVersions}
                      isExpanded={expanded.includes(titleKey)}
                      onExpandChange={() => toggleExpand(titleKey)}
                      onVersionClick={onVersionClick}
                    />
                  );
                })}
              </TitleContainer>
            );
          })}
          {incompatibleVersionSelectionConfirmation}
        </>
      )}
    </Box>
  );
};

export default ScheduleSelectedVersions;

const TitleContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: 0,
  marginBottom: theme.spacing(4)
}));

const TitleName = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#D9D9D9' : darken('#D9D9D9', 0.7),
  fontSize: theme.typography.pxToRem(18),
  fontWeight: 500
}));

const SelectedEpisodeCount = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.primary, 0.5),
  fontSize: theme.typography.pxToRem(16)
}));
